import { C, decades, type DecadeDef, type Effect } from "@bd/content";
import { isYearStart, yearOf } from "./calendar";
import { addDecision, DECISION_HANDLERS } from "./company";
import { applyEffect, eventCash, eventScales, roundNice } from "./director";
import type { Company, GameState, PendingDecision } from "./state";
import { digits, log, money } from "./util";

/**
 * Decade decisions: at the start of each decade (1970 to 2030) one bigger choice tied to the era, such as
 * mail order or toy chains, computers in the office, the collectible-card bet, a web shop, backers or screens,
 * greener boxes, and what to leave behind. Content in decades.json; effects are the events' effects, mostly
 * modifiers that last the decade.
 */

/** The decade choice for a year (1970, 1980, ...), if there is one. */
export function decadeFor(year: number): DecadeDef | undefined {
  return decades.find((d) => d.year === year);
}

function first(effects: Effect[], type: string): Effect | undefined {
  return effects.find((e) => e.type === type);
}

/** The line under a choice with the amounts it really applies, sized to this company (data holds the scales). */
export function decadeDetail(detail: string, effects: Effect[], week: number, data: Record<string, any>): string {
  const cash = first(effects, "cash");
  const fans = first(effects, "fans");
  const research = first(effects, "research");
  const royalty = first(effects, "royalty");
  return detail
    .replace("{cash}", cash ? money(Math.abs(eventCash(cash.amount, week, data.cashScale ?? 1))) : "")
    .replace("{fans}", fans ? digits(roundNice(fans.amount * (data.fanScale ?? 1))) : "")
    .replace("{research}", research ? digits(Math.round(research.amount * (data.researchScale ?? 1))) : "")
    .replace("{royalty}", royalty ? money(Math.abs(eventCash(royalty.weekly, week))) : "");
}

/** Put this decade's choice to a company (once per decade). */
export function offerDecade(state: GameState, co: Company, def: DecadeDef): PendingDecision | null {
  if (co.decades?.[def.id] || co.pending.some((d) => d.kind === "decade" && d.data?.decade === def.id)) return null;
  const data = { decade: def.id, ...eventScales(state, co) };
  return addDecision(state, co, {
    kind: "decade",
    title: def.title,
    text: def.text,
    options: def.options.map((o) => ({ id: o.id, label: o.label, detail: decadeDetail(o.detail, o.effects, state.week, data) })),
    defaultOption: def.default,
    weeks: 8,
    data,
  });
}

DECISION_HANDLERS.decade = (state, co, d, option) => {
  const def = C.decade[d.data?.decade];
  const o = def?.options.find((x) => x.id === option);
  if (!def || !o || co.decades?.[def.id]) return;
  co.decades = { ...(co.decades ?? {}), [def.id]: o.id };
  for (const e of o.effects) applyEffect(state, co, e, d.data ?? {}, def.title);
  log(state, "decade", `${co.name} ${o.log}`, co.id);
};

export const decadeSystem = {
  name: "decades",
  week(state: GameState): void {
    if (!isYearStart(state.week)) return;
    const def = decadeFor(yearOf(state.week));
    if (!def) return;
    for (const co of state.companies) if (!co.bankrupt) offerDecade(state, co, def);
  },
};

/** For the interface: the decade choices this company has made, oldest first, with the option's label. */
export function decadeHistory(co: Company): { year: number; title: string; choice: string }[] {
  return decades.filter((d) => co.decades?.[d.id]).map((d) => ({ year: d.year, title: d.title, choice: d.options.find((o) => o.id === co.decades![d.id])?.label ?? "" }));
}

